import React from 'react';
import { Plus, Search, User, ChevronLeft, ChevronRight, Workflow, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatTaskDate } from '../lib/taskGrid';
import { TaskUserFilter } from './TaskUserFilter';

export function TaskHoyToolbar({
    activeTab, setActiveTab,
    timeRange, setTimeRange,
    dateOffset, setDateOffset,
    currentRange,
    triggerSearchQuery, setTriggerSearchQuery, showTriggerSearch, setShowTriggerSearch,
    focusedTriggerIdx, setFocusedTriggerIdx, triggerSearchTasks, handleTriggerSelect,
    roadmapSearchQuery, setRoadmapSearchQuery, showRoadmapSearch, setShowRoadmapSearch,
    focusedRoadmapTaskIdx, setFocusedRoadmapTaskIdx, roadmapSearchTasks, handleRoadmapTaskSelect,
    searchQuery, setSearchQuery,
    showRoadmapTriggers, setShowRoadmapTriggers,
    activeSpaceId, userSpaces, createSpaceDropdownOpen, setCreateSpaceDropdownOpen,
    handleCreateNewTask, getTaskPermissions,
    showSubtasksAsNested, onToggleShowSubtasksAsNested,
    showCompletedTasks, onToggleShowCompleted,
    toast, // Fase 5.17 -- threadeado a `TaskUserFilter`
    isWorkframeRoute,
}) {
    const { t: translate } = useTranslation('tasks');

    return (
        <div className="tabs-header-list top-tabs" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '15px', position: 'sticky', top: '-20px', margin: '-20px -20px 0 -20px', zIndex: 1000, backgroundColor: '#000000', padding: '15px 20px 6px 20px', borderBottom: '1px solid #1a1a1a' }}>
            
            {/* TOP ROW: Filters and Navigation */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                
                {/* Left Side: View Modes */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div className="tabs-buttons-group">
                        {isWorkframeRoute ? (
                            ['estructurada', 'tablero'].map(tab => (
                                <button
                                    key={tab}
                                    className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab === 'estructurada' ? 'Estructurada' : translate(`tabs.${tab}`)}
                                </button>
                            ))
                        ) : (
                            ['lista', 'tablero', 'flow'].map(tab => (
                                <button
                                    key={tab}
                                    className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {translate(`tabs.${tab}`)}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Side: Date & Users Filters */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    
                    {!isWorkframeRoute && (
                        <>
                            {/* Time Filters Tag */}
                            <div style={{ background: '#1a1a1a', borderRadius: 'var(--radius-md)', padding: '2px', display: 'flex', gap: '2px' }}>
                                {[
                                    { id: 'hoy', label: translate('tabs.today') },
                                    { id: 'week', label: translate('tabs.week') },
                                    { id: 'month', label: translate('tabs.month') },
                                    { id: '3_months', label: translate('tabs.threeMonths') },
                                    { id: '6_months', label: '6 Meses' },
                                    { id: '1_year', label: translate('tabs.oneYear') },
                                    { id: 'custom', label: translate('tabs.custom') }
                                ].map(tr => (
                                    <button
                                        key={tr.id}
                                        onClick={() => setTimeRange(tr.id)}
                                        style={{
                                            background: timeRange === tr.id ? '#2e2e2e' : 'transparent',
                                            color: timeRange === tr.id ? '#fff' : '#888',
                                            border: '1px solid ' + (timeRange === tr.id ? '#444' : 'transparent'),
                                            borderRadius: 'calc(var(--radius-md) - 2px)',
                                            padding: 'var(--space-1) var(--space-2)',
                                            fontSize: '11px',
                                            height: '24px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {tr.label}
                                    </button>
                                ))}
                            </div>

                            {/* Date Navigation Tag */}
                            {timeRange !== 'custom' && (
                                <div style={{ display: 'flex', alignItems: 'center', background: '#1a1a1a', borderRadius: 'var(--radius-md)', padding: '2px' }}>
                                    <button onClick={() => setDateOffset(prev => prev - 1)} style={{ padding: '0 4px', background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', height: '24px', display: 'flex', alignItems: 'center', borderRadius: 'var(--radius-md) 0 0 var(--radius-md)' }}>
                                        <ChevronLeft size={16} />
                                    </button>
                                    <div style={{ background: '#2e2e2e', padding: '0 12px', height: '24px', display: 'flex', alignItems: 'center', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                                        {timeRange === 'hoy'
                                            ? new Date(currentRange.startStr + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '')
                                            : `${new Date(currentRange.startStr + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '')} - ${new Date(currentRange.endStr + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '')}`
                                        }
                                    </div>
                                    <button onClick={() => setDateOffset(prev => prev + 1)} style={{ padding: '0 4px', background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', height: '24px', display: 'flex', alignItems: 'center', borderRadius: '0 var(--radius-md) var(--radius-md) 0' }}>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}

                            {dateOffset !== 0 && timeRange !== 'custom' && (
                                <button
                                    onClick={() => setDateOffset(0)}
                                    style={{
                                        padding: '0 var(--space-2)', background: 'rgba(255,255,255,0.1)', border: 'none',
                                        color: '#fff', cursor: 'pointer', height: '28px', display: 'flex',
                                        alignItems: 'center', borderRadius: 'var(--radius-md)', fontSize: '11px', fontWeight: 'bold'
                                    }}
                                >
                                    Ir a Hoy
                                </button>
                            )}
                        </>
                    )}

                    <TaskUserFilter toast={toast} />
                </div>
            </div>

            {/* BOTTOM ROW: Full Width Search & Actions */}
            {!isWorkframeRoute && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', height: '28px', position: 'relative', width: '100%' }}>
                    
                    {/* Mostrar Subtareas Filter */}
                    <button
                        onClick={() => onToggleShowSubtasksAsNested && onToggleShowSubtasksAsNested()}
                        style={{
                            height: '28px',
                            padding: '0 12px',
                            fontSize: '11px',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            backgroundColor: showSubtasksAsNested ? '#a855f7' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                        }}
                    >
                        {showSubtasksAsNested ? 'Mostrar subtareas' : 'Subtareas off'}
                    </button>

                    {/* Check Filter (hide completed) */}
                    <button
                        onClick={() => onToggleShowCompleted && onToggleShowCompleted()}
                        style={{
                            height: '28px',
                            padding: '0 12px',
                            fontSize: '11px',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            backgroundColor: !showCompletedTasks ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                        }}
                    >
                        {!showCompletedTasks ? 'check' : 'uncheck'}
                    </button>
                    
                    {/* Search Bar - Full Width */}
                    <div style={{ flex: 1 }}>
                        {activeTab === 'flow' ? (
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div className="task-search-bar task-search-bar-roadmap" style={{ height: '28px', width: '100%', boxSizing: 'border-box', position: 'relative' }}>
                                    <Search size={11} className="search-icon" color="#fff" />
                                    <input
                                        type="text"
                                        placeholder={"Buscar Elementos..."}
                                        aria-label={"Buscar Elementos"}
                                        value={triggerSearchQuery}
                                        onChange={(e) => {
                                            setTriggerSearchQuery(e.target.value);
                                            setShowTriggerSearch(true);
                                            setFocusedTriggerIdx(-1);
                                        }}
                                        onFocus={() => setShowTriggerSearch(true)}
                                        onBlur={() => setTimeout(() => setShowTriggerSearch(false), 200)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                setFocusedTriggerIdx(prev => Math.min(prev + 1, triggerSearchTasks.length - 1));
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                setFocusedTriggerIdx(prev => Math.max(prev - 1, -1));
                                            } else if (e.key === 'Enter') {
                                                if (focusedTriggerIdx >= 0 && triggerSearchTasks[focusedTriggerIdx]) {
                                                    handleTriggerSelect(triggerSearchTasks[focusedTriggerIdx]);
                                                }
                                            }
                                        }}
                                        className="task-search-input"
                                        style={{ height: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', color: '#fff', width: '100%' }}
                                    />
                                    {showTriggerSearch && (triggerSearchQuery.trim() !== '' || triggerSearchTasks.length > 0) && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, width: '100%', marginTop: 'var(--space-1)',
                                            backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 'var(--radius-md)',
                                            maxHeight: '300px', overflowY: 'auto', zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                                        }}>
                                            {triggerSearchTasks.length === 0 ? (
                                                <div style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '12px', color: '#888' }}>No hay elementos en el mapa</div>
                                            ) : (
                                                triggerSearchTasks.map((rt, idx) => {
                                                    const isFocused = idx === focusedTriggerIdx;
                                                    let iconEmoji = '🔹';
                                                    let iconBg = '#333';
                                                    if (rt.category === 'etapa' || rt.type === 'etapa') {
                                                        iconEmoji = '🤝'; iconBg = '#ec4899';
                                                    } else if (rt.category === 'texto' || rt.type === 'texto') {
                                                        if (rt.sub === 'post-it') {
                                                            iconEmoji = '📝'; iconBg = '#eab308';
                                                        } else {
                                                            iconEmoji = 'Aa'; iconBg = '#3b82f6';
                                                        }
                                                    } else if (rt.type === 'image') {
                                                        iconEmoji = '🖼️'; iconBg = '#a855f7';
                                                    }
                                                    return (
                                                        <div key={rt.id} onClick={() => handleTriggerSelect(rt)} onMouseEnter={() => setFocusedTriggerIdx(idx)} style={{ padding: 'var(--space-2) var(--space-3)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: isFocused ? '#2a2a2a' : 'transparent', borderBottom: '1px solid #222' }}>
                                                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: iconBg, color: '#fff', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold' }}>{iconEmoji}</div>
                                                            <div style={{ flex: 1, fontSize: '11px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rt.label || translate('common.untitled')}</div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="task-search-bar task-search-bar-roadmap" style={{ height: '28px', width: '100%', boxSizing: 'border-box', position: 'relative' }}>
                                    <Search size={11} className="search-icon" color="#fff" />
                                    <input
                                        type="text"
                                        placeholder={translate('flow.searchPlaceholder')}
                                        aria-label={translate('flow.searchAriaLabel')}
                                        value={roadmapSearchQuery}
                                        onChange={(e) => {
                                            setRoadmapSearchQuery(e.target.value);
                                            setShowRoadmapSearch(true);
                                            setFocusedRoadmapTaskIdx(-1);
                                        }}
                                        onFocus={() => setShowRoadmapSearch(true)}
                                        onBlur={() => setTimeout(() => setShowRoadmapSearch(false), 200)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                setFocusedRoadmapTaskIdx(prev => Math.min(prev + 1, roadmapSearchTasks.length - 1));
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                setFocusedRoadmapTaskIdx(prev => Math.max(prev - 1, -1));
                                            } else if (e.key === 'Enter') {
                                                if (focusedRoadmapTaskIdx >= 0 && roadmapSearchTasks[focusedRoadmapTaskIdx]) {
                                                    handleRoadmapTaskSelect(roadmapSearchTasks[focusedRoadmapTaskIdx]);
                                                }
                                            }
                                        }}
                                        className="task-search-input"
                                        style={{ height: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', color: '#fff', width: '100%' }}
                                    />
                                    {showRoadmapSearch && (roadmapSearchQuery.trim() !== '' || roadmapSearchTasks.length > 0) && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, width: '100%', marginTop: 'var(--space-1)',
                                            backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 'var(--radius-md)',
                                            maxHeight: '300px', overflowY: 'auto', zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                                        }}>
                                            {roadmapSearchTasks.length === 0 ? (
                                                <div style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '12px', color: '#888' }}>{translate('flow.noTasks')}</div>
                                            ) : (
                                                roadmapSearchTasks.map((rt, idx) => {
                                                    const hasDate = Boolean(rt.date || rt.start_time);
                                                    const isFocused = idx === focusedRoadmapTaskIdx;
                                                    return (
                                                        <div key={rt.id} onClick={() => handleRoadmapTaskSelect(rt)} onMouseEnter={() => setFocusedRoadmapTaskIdx(idx)} style={{ padding: 'var(--space-2) var(--space-3)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: isFocused ? '#2a2a2a' : 'transparent', borderBottom: '1px solid #222' }}>
                                                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: hasDate ? '#3b82f6' : '#666' }}></div>
                                                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#444', overflow: 'hidden', flexShrink: 0 }}>
                                                                {rt.assignee?.image ? (
                                                                    <img src={rt.assignee.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" loading="lazy" />
                                                                ) : (
                                                                    <User size={10} style={{ margin: 'var(--space-1)' }} color="#aaa" />
                                                                )}
                                                            </div>
                                                            <div style={{ flex: 1, fontSize: '11px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rt.title || translate('common.untitled')}</div>
                                                            <div style={{ fontSize: '9px', color: '#888', padding: 'var(--space-1) var(--space-1)', background: '#222', borderRadius: 'var(--radius-sm)', flexShrink: 0 }}>{rt.stage || translate('flow.noStage')}</div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="task-search-bar" style={{ height: '28px', width: '100%', boxSizing: 'border-box' }}>
                                <Search size={11} className="search-icon" color="#fff" />
                                <input
                                    type="text"
                                    placeholder={translate('toolbar.searchPlaceholder')}
                                    aria-label={translate('toolbar.searchAriaLabel')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="task-search-input"
                                    style={{ height: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', color: '#fff', width: '100%' }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Right Side Actions: Triggers Toggle & Create Task & Create Suggestion */}
                    {activeTab === 'flow' && (
                        <button
                            onClick={() => setShowRoadmapTriggers(!showRoadmapTriggers)}
                            className="create-task-btn"
                            style={{
                                height: '28px',
                                padding: '0 var(--space-3)',
                                background: showRoadmapTriggers ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                borderRadius: '50px',
                                flexShrink: 0
                            }}
                        >
                            <Workflow size={14} />
                            <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Triggers</span>
                        </button>
                    )}

                    {!activeSpaceId ? (
                        userSpaces.filter(s => s.name !== 'all' && s.permissions?.task_create !== false).length > 0 && (
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                <button
                                    className="create-task-button"
                                    onClick={() => setCreateSpaceDropdownOpen(!createSpaceDropdownOpen)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '0 var(--space-3)',
                                        height: '28px',
                                        boxSizing: 'border-box',
                                        backgroundColor: '#3b82f6',
                                        border: '1px solid #2563eb',
                                        borderRadius: 'var(--radius-md)',
                                        color: '#fff',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                                >
                                    <Plus size={14} />
                                    Crear en...
                                </button>
                                {createSpaceDropdownOpen && (
                                    <>
                                        <div
                                            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                                            onClick={() => setCreateSpaceDropdownOpen(false)}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: 0,
                                            marginTop: 'var(--space-1)',
                                            backgroundColor: '#1f1f1f',
                                            border: '1px solid #333',
                                            borderRadius: 'var(--radius-md)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                            zIndex: 1000,
                                            minWidth: '160px',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <div style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '10px', color: '#888', borderBottom: '1px solid #333', fontWeight: 600 }}>
                                                SELECCIONA UN ESPACIO
                                            </div>
                                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                {userSpaces.filter(s => s.name !== 'all' && s.permissions?.task_create !== false).length === 0 ? (
                                                    <div style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '12px', color: '#666' }}>No hay espacios</div>
                                                ) : (
                                                    userSpaces.filter(s => s.name !== 'all' && s.permissions?.task_create !== false).map(space => (
                                                        <div
                                                            key={space.id}
                                                            onClick={() => {
                                                                setCreateSpaceDropdownOpen(false);
                                                                handleCreateNewTask(space);
                                                            }}
                                                            style={{
                                                                padding: 'var(--space-2) var(--space-3)',
                                                                fontSize: '12px',
                                                                color: '#ddd',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                transition: 'background-color 0.2s'
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        >
                                                            {space.name}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    ) : getTaskPermissions(activeSpaceId)?.task_create !== false ? (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

                            <button
                                className="create-task-button"
                                onClick={() => handleCreateNewTask()}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '0 var(--space-3)',
                                    height: '28px',
                                    boxSizing: 'border-box',
                                    backgroundColor: '#3b82f6',
                                    border: '1px solid #2563eb',
                                    borderRadius: 'var(--radius-md)',
                                    color: '#fff',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                            >
                                <Plus size={14} />
                                {translate('toolbar.createButton')}
                            </button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
}
